import { failure, ok, pending, Result } from "@/common/common";
import { AxiosInstance } from "axios";
import { makeAutoObservable } from "mobx";
import { Language } from "@/i18n/languages";

import { TMessage } from "@/i18n/tt";
import { NumberFieldModel, RadioFieldModel } from "@/common/commonForms";
import { ShareType, ShareTypeCode, shareTypeLabel } from "@/common/share";
import {
  ShareTypeNameModel,
  toString,
} from "@/shareholderRegister/shareClasses/shareClassesDomain";

export class NewShareClassController {
  organisationNumber: string;
  shareTypeApi: ShareClassApi;
  onClose: () => void;
  shareTypeModel: RadioFieldModel<ShareTypeCode | typeof CustomShareClass>;
  reportingShareTypeModel: RadioFieldModel<ShareTypeCode>;
  numberOfVotesModel: NumberFieldModel;
  addShareTypeResult: Result<void, TMessage> | undefined = undefined;
  shareTypeNames: ShareTypeNameModel[];

  constructor(
    organisationNumber: string,
    shareTypeApi: ShareClassApi,
    onCancel: () => void,
    availableShareTypes: ShareTypeCode[],
    locale: Language,
    private onFinish: (newShareType: ShareType) => void,
  ) {
    this.organisationNumber = organisationNumber;
    this.shareTypeApi = shareTypeApi;
    this.onClose = onCancel;

    // filter out special case share types (EXTRAORDINARY K, L, M ...) since they are not usually used
    const visibleShareTypes = availableShareTypes.filter((code) => !code.includes("_"));

    this.shareTypeModel = new RadioFieldModel<ShareTypeCode | typeof CustomShareClass>({
      required: true,
      options: [...visibleShareTypes, CustomShareClass],
      value: visibleShareTypes[0],
      idFn: (option) => option,
      labelFn: (option) =>
        option === CustomShareClass
          ? { key: "shareType.custom-shareclass" }
          : shareTypeLabel(option),
      iconFn: (option) => (option === CustomShareClass ? "gear" : undefined),
    });
    this.reportingShareTypeModel = new RadioFieldModel<ShareTypeCode>({
      required: true,
      options: visibleShareTypes,
      value: visibleShareTypes[0],
      idFn: (option) => option,
      labelFn: (option) => shareTypeLabel(option),
    });
    this.numberOfVotesModel = new NumberFieldModel({
      required: true,
      isPositiveOrZero: true,
      locale: locale,
      maxDecimals: 5,
      value: 1,
    });
    this.shareTypeNames = Object.values(Language).map(
      (language) => new ShareTypeNameModel(language),
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }

  *createShareType() {
    this.validate();
    if (this.isValid() && this.numberOfVotesModel.parsedValue !== undefined) {
      this.addShareTypeResult = pending();
      try {
        const newShareType: ShareType = yield this.shareTypeApi.createShareClass({
          organizationnumber: this.organisationNumber,
          vote: this.numberOfVotesModel.parsedValue,
          shareTypeCode: this.isCustomName
            ? this.reportingShareTypeModel.value
            : (this.shareTypeModel.value as ShareTypeCode),
          shareTypeNames: this.isCustomName
            ? this.shareTypeNames.map((model) => ({
                language: model.language,
                name: model.name.validatedValue,
                separator: toString(model.separator.value),
              }))
            : undefined,
        });
        this.addShareTypeResult = ok();
        if (newShareType !== undefined) {
          this.onFinish(newShareType);
        }
        this.onClose();
      } catch (e) {
        this.addShareTypeResult = failure({ key: "shareClasses.shareClassForm.save.error" });
      }
    }
  }

  validate() {
    this.numberOfVotesModel.validate();
    this.shareTypeModel.validate();
    this.shareTypeNames.forEach((model) => model.name.validate());
  }

  isValid() {
    return (
      this.numberOfVotesModel.isValid &&
      this.shareTypeModel.isValid &&
      (!this.isCustomName || this.shareTypeNames.every((model) => model.name.isValid))
    );
  }

  get isCustomName() {
    return this.shareTypeModel.value === CustomShareClass;
  }
}

export const CustomShareClass = "CustomShareClass";

export class ShareClassApi {
  private http: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.http = axiosInstance;
  }

  createShareClass(request: CreateShareClassRequest): Promise<ShareType> {
    return this.http.post(`/sharetype/new`, request).then((res) => res.data);
  }
}

export interface CreateShareClassRequest {
  organizationnumber: string;
  vote: number;
  shareTypeCode: ShareTypeCode;
  shareTypeNames?: {
    language: Language;
    name: string;
    separator: string;
  }[];
}
